import { Injectable } from '@angular/core';
import { DateTimeUtilities } from '@benefitsui/app/shared/utilities/dateTimeUtilities';
import { ClaimStatusEnum, SearchSortDirection, SymetraDateTime } from '@benefitsui/app/api/benefits-clients';
import {
  getClaimTypeDisplayUi,
  IClaimData,
  IClaimSearchParameters,
  IPaginatedClaimsData
} from './claim-data.types';
import { ClaimDataRaw } from './claim-data-raw.types';
import localClaimsData from './local-claims-data.json';
import { ClaimLeaveTypeEnum, ClientClaimFieldEnum } from './claim-data-enums';

@Injectable({
  providedIn: 'root'
})
export class LocalClaimsDataReaderService {
  private readonly data: IClaimData[] = [];

  constructor() {
    const claimsDataRaw = this.getData();
    this.data = this.transformDataSet(claimsDataRaw);
  }

  getPaginatedClaimsData(searchParams: IClaimSearchParameters): IPaginatedClaimsData {
    let filteredData = [...this.data];

    // Apply search text filtering if provided
    if (searchParams.searchText) {
      const searchTermLower = searchParams.searchText.toLowerCase();
      filteredData = filteredData.filter((claim) => {
        const firstName = claim.employee.firstName.toLowerCase();
        const lastName = claim.employee.lastName.toLowerCase();
        const fullName = `${lastName}, ${firstName}`.toLowerCase();
        const firstLastFormat = `${firstName} ${lastName}`.toLowerCase();
        const claimId = claim.claimDetails.claimId.toLowerCase();

        // Return true if any of these fields match the search term
        return (
          firstName.includes(searchTermLower) ||
          lastName.includes(searchTermLower) ||
          fullName.includes(searchTermLower) ||
          firstLastFormat.includes(searchTermLower) ||
          claimId.includes(searchTermLower)
        );
      });
    }

    // :  Filter claim types by multi-select options
    if (searchParams.filters.claimTypes && searchParams.filters.claimTypes.length > 0) {
      const selectedTypes = searchParams.filters.claimTypes.map(option => option.label || option.key as string);

      filteredData = filteredData.filter(claim => {
        const claimTypeIncludedArray = claim.claimType.claimTypes.map(type => selectedTypes.includes(type));
        const claimTypeIncluded = claimTypeIncludedArray.includes(true);
        return claimTypeIncluded;
      });
    }

    // Apply sorting based on sort parameters
    if (searchParams.sortField) {
      filteredData = this.sortClaimsData(filteredData, searchParams.sortField, searchParams.sortDirection);
    }

    // Get total count before pagination
    const totalResults = filteredData.length;

    // Apply pagination
    const startIndex = (searchParams.page - 1) * searchParams.pageSize;
    const endIndex = startIndex + searchParams.pageSize;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    return {
      claims: paginatedData,
      totalResults: totalResults,
      currentPage: searchParams.page,
      resultsPerPage: searchParams.pageSize
    };
  }

  // TODO: I would expect this to be handled in the backend logic
  private sortClaimsData(data: IClaimData[], sortField: ClientClaimFieldEnum, sortDirection: SearchSortDirection): IClaimData[] {
    return [...data].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case ClientClaimFieldEnum.Name:
          // Sort by last name, then first name
          comparison = a.employee.lastName.localeCompare(b.employee.lastName);
          if (comparison === 0) {
            comparison = a.employee.firstName.localeCompare(b.employee.firstName);
          }
          break;
        case ClientClaimFieldEnum.ClaimType:
          comparison = a.claimType.claimTypeLabel.localeCompare(b.claimType.claimTypeLabel);
          break;
        case ClientClaimFieldEnum.ClaimId:
          comparison = a.claimDetails.claimId.localeCompare(b.claimDetails.claimId);
          break;
        case ClientClaimFieldEnum.Status:
          comparison = a.status.localeCompare(b.status);
          break;
        case ClientClaimFieldEnum.Received:
          // Compare dates - convert to numbers for comparison
          comparison = this.compareDates(a.received, b.received);
          // If dates are equal, sort by last name (always ascending)
          if (comparison === 0) {
            return a.employee.lastName.localeCompare(b.employee.lastName);
          }
          break;
        case ClientClaimFieldEnum.Started:
          // Compare dates - convert to numbers for comparison
          comparison = this.compareDates(a.started.claimDate, b.started.claimDate);
          break;
        case ClientClaimFieldEnum.Division:
          comparison = a.division.name.localeCompare(b.division.name);
          break;
        case ClientClaimFieldEnum.LeaveType: {
          const aLeaveType = a.leaveType || '';
          const bLeaveType = b.leaveType || '';
          comparison = aLeaveType.localeCompare(bLeaveType);
          break;
        }
        default:
          // Default to sorting by name
          comparison = a.employee.lastName.localeCompare(b.employee.lastName);
      }

      // Apply sort direction
      return sortDirection === SearchSortDirection.Ascending ? comparison : -comparison;
    });
  }

  private compareDates(dateA: SymetraDateTime, dateB: SymetraDateTime): number {
    if (!dateA && !dateB) return 0;
    if (!dateA) return -1;
    if (!dateB) return 1;

    // Convert to Date objects for comparison
    const dateAObj = DateTimeUtilities.symetraDateTimeToDate(dateA);
    const dateBObj = DateTimeUtilities.symetraDateTimeToDate(dateB);

    const dateDifference = dateAObj.getTime() - dateBObj.getTime();

    return dateDifference;
  }

  private getData(): ClaimDataRaw[] {
    return localClaimsData as ClaimDataRaw[];
  }

  private transformDataRow(dataRow: ClaimDataRaw): IClaimData {
    const employee = {
      firstName: dataRow.employee.firstName,
      lastName: dataRow.employee.lastName,
      birthdate: DateTimeUtilities.dateStringToSymetraDateTime(dataRow.employee.birthdate),
      ssn: dataRow.employee.ssn,
      gender: dataRow.employee.gender,
      workState: dataRow.employee.workState,
      caseManager: dataRow.employee.caseManager
    };

    const claimDateInfo = {
      claimDate: DateTimeUtilities.dateStringToSymetraDateTime(dataRow.started.claimDate)
    };

    const claimTypeUi = getClaimTypeDisplayUi(dataRow.claimInfo, dataRow.wop || false, dataRow.planCodes);

    const claimData: IClaimData = {
      claimType: claimTypeUi,
      claimDetails: dataRow.claimDetails,
      claimInfo: dataRow.claimInfo,
      benefits: dataRow.benefits,
      division: dataRow.division,
      employee: employee,
      keyDates: dataRow.keyDates,
      leaveType: dataRow.leaveType ? (dataRow.leaveType as ClaimLeaveTypeEnum) : undefined,
      payments: dataRow.payments,
      progressTracker: dataRow.progressTracker,
      received: DateTimeUtilities.dateStringToSymetraDateTime(dataRow.received),
      relatedClaims: dataRow.relatedClaims ? this.transformDataSet(dataRow.relatedClaims) : [],
      started: claimDateInfo,
      status: dataRow.status as ClaimStatusEnum,
      wop: dataRow.wop // Preserve the WOP flag for detail page transforms
    };

    return claimData;
  }

  private transformDataSet(data: ClaimDataRaw[]): IClaimData[] {
    const dataArray = data.map((item) => this.transformDataRow(item));
    return dataArray;
  }

  // Method to get a claim by ID
  getClaimById(claimId: string): IClaimData | null {
    return this.data.find(claim => claim.claimDetails.claimId === claimId) || null;
  }
}
